(function() {
  window.Home = window.Home || {}

  async function asyncForEach(array, callback) {
    for (let index = 0; index < array.length; index++) {
      await callback(array[index], index, array)
    }
  }

  function chunkArray(array, chunkSize) {
    const newArray = []
    for (let i = 0; i < array.length; i += chunkSize) {
      newArray.push(array.slice(i, i + chunkSize))
    }
    return newArray
  }

  /**
   * The RecommendedTasks class is used to manage the paging context for data
   * from the POP service. We use a class so that multiple instances of it can
   * be created for different lists.
   *
   * @param {String} filter Filter type: 'all', 'temple', 'record'
   * @returns {void}
   */
  function RecommendedTasks(filter = '') {
    // We group data from the POP service by personId and save them here.
    this.personsWithTasks = []

    // This is the list of streamed tasks before being transformed and grouped by personId
    this.streamedTasks = []

    // An index into the personsWithTasks array representing the next element
    // that hasn't been requested by the list which consumes this service.
    this.currentPersonIndex = 0

    this.noMoreTasks = false
    if (typeof filter !== 'string') this.filter = 'all'
    else this.filter = filter.toLowerCase() || 'all'
  }

  /**
   * Get next batch of recommended tasks. Here we decided whether we need to
   * request more data from the POP service.
   *
   * @param {number} numToGet number of tasks to get
   * @returns {object} an object of tasks
   */
  RecommendedTasks.prototype.getBatch = async function(numToGet) {
    await setUserTasks()
    if (!this.personsWithTasks.length && !this.noMoreTasks) {
      this.personsWithTasks = [ ...getFilteredTasks(this.filter) ]
    }
    const tasks = await this.getTasks(numToGet)
    // Before returning the data we calculate whether we have any more left.
    // Consuming clients can then check the noMoreTasks flag if they want to.
    this.noMoreTasks = this.currentPersonIndex >= this.personsWithTasks.length
    return tasks
  }

  /**
   * Get and return the given number of tasks, if we have that many available.
   *
   * @param {number} numToSend the number of tasks to get
   * @returns {object} a tasks object
   */
  RecommendedTasks.prototype.getTasks = function(numToSend) {
    let tasksToSend = []
    let taskAmount = numToSend
    // If we don't have numToSend tasks then send what we can, if we can
    if (this.personsWithTasks.length < numToSend + this.currentPersonIndex) {
      taskAmount = this.personsWithTasks.length - this.currentPersonIndex
      if (taskAmount < 0) {
        taskAmount = 0
      }
    }
    tasksToSend = taskAmount === 0
      ? []
      : this.personsWithTasks.slice(this.currentPersonIndex, this.currentPersonIndex + taskAmount)

    // Since personsWithTasks.slice's start parameter is inclusive, currentPersonIndex is the index of the nextTask we'll send
    this.currentPersonIndex += taskAmount

    if (FS.showEx('forceEmptyStateTasksEx')) tasksToSend = []
    return tasksToSend
  }

  /**
   * Returns user tasks by filter type
   *
   * @param {string} filter filter type
   * @returns {any} filtered tasks
   */
  function getFilteredTasks(filter = '') {
    if (filter.toLowerCase() === 'record') return Home.RecommendedTasks.recordTaskData || []
    else if (filter.toLowerCase() === 'temple') return Home.RecommendedTasks.templeTaskData || []
    return Home.RecommendedTasks.unfilteredTaskData || []
  }

  /**
   * Dismisses a recommended task from POP
   *
   * @param {object} task task to be dismissed
   * @returns {void}
   */
  function dismissTask(task) {
    toggleDismissTask(task, true)
  }

  /**
   * Undismisses a recommended task from POP
   *
   * @param {object} task task to be undismissed
   * @returns {void}
   */
  function undoDismissTask(task) {
    toggleDismissTask(task, false)
  }

  /**
   * Undismisses a recommended task from POP
   *
   * @param {object} task task to be either dismissed or undismissed
   * @param {boolean} shouldDismiss true/false should we dismiss this task
   * @returns {void}
   */
  function toggleDismissTask(task, shouldDismiss) {
    let url = `/service/home/pop/users/${FS.User.getEffectiveId()}/opportunities?personId=${task.personId}&resourceUri=${task.resourceUri}&type=${task.type}`
    if (shouldDismiss) {
      url += "&status=DISMISSED"
    }
    Home.xhr.patch(url).catch(err => {
      //This error is not handled because the data has already been deleted from the database
      console.error(err)
    })
  }

  /**
   * Refresh opportunity data for a given PID.
   *
   * @param {string} pid personId to refresh
   * @param {string} oppType type of opportunity to refresh
   * @returns {void}
   */
  function refreshOpportunities(pid, oppType) {
    return Home.xhr.post('/service/home/pie/events', {
      type: 'REFRESH',
      body: {
        personIds: [pid],
        opportunityType: oppType
      }
    }).catch(err => {
      //This function is part of the error handling of another function
      console.error(err)
    })
  }

  /**
   * Checks if there is a query param override. This check matches the query param used in fsh-card-feed
   *
   * @returns {boolean} if there is a query param override it returns a boolean, otherwise it returns undefined
   */
  function getIsNewUserOverride(){
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('feedType')) {
      return urlParams.get('feedType').toUpperCase().includes('NEW')
    }
  }

  /**
   * This is used to format the data from POP to meet the needs to the application
   *
   * @param {object} originalPerson the original person as received from POP
   * @returns {object} formated person
   */
  // eslint-disable-next-line complexity
  function formatDataMap(originalPerson) {
    const person = {
      personId: originalPerson.personId,
      id: originalPerson.personId, //duplicate field because some of the depedancies expect "id" instead of "personId". Keeping "personId" because it's more descriptive.
      opportunities: [],
      types: []
    }
    if ( this.filter !== 'record'
      && originalPerson.hasTemple
      && originalPerson.templeType !== 'DUPLICATE_PERSON'
      && originalPerson.templeType !== 'NEED_PERMISSION' ) {

      person.hasTemple = true
      const templeOpportunity = {
        type: 'TEMPLE',
        personId: originalPerson.personId,
        resourceUri: originalPerson.personId //for a temple opportunity the personId & resourceUri are the same
      }
      person.opportunities.push(templeOpportunity)
      // Converting RESERVED_SHARED_READY to READY since the behavior is the same.  This avoids having to handle RESERVED_SHARED_READY everywhere else in the code.
      if (originalPerson.templeType === 'RESERVED_SHARED_READY') {
        originalPerson.templeType = 'READY'
      }
      if (originalPerson.templeType === 'READY') {
        person.types.push('green')
      } else if (originalPerson.templeType === 'NEED_MORE_INFORMATION') {
        person.types.push('yellow')
      }
    }
    if (this.filter !== 'temple' && originalPerson.hasRecord) {
      person.hasRecord = true
      const records = [
        ...originalPerson.recordTypes.OTHER,
        ...originalPerson.recordTypes.NEW_SOURCE,
        ...originalPerson.recordTypes.NEW_PERSON,
        ...originalPerson.recordTypes.NEW_CONCLUSION
      ]
      .filter((item, index, array) => array.indexOf(item) === index) // deduping
      .map(record => {
        return {
          type: 'RECORD',
          personId: originalPerson.personId,
          resourceUri: record
        }
      })
      person.opportunities = [...person.opportunities, ...records]
    }
    return person
  }

  /**
   * Filter the opportunities by filter type
   *
   * @param {any} opportunities opportunities
   * @param {string} filter filter type
   * @returns {any} filtered opportunities
   */
  function getFilteredOpportunities(opportunities, filter) {
    const isTemple = filter.toLowerCase() === 'temple'
    const filterType = isTemple ? 'hasTemple' : 'hasRecord'
    const otherFilterType = isTemple ? 'hasRecord' : 'hasTemple'
    const filteredOpportunities = [ ...opportunities ]
    return filteredOpportunities.filter(person => {
      if (person[filterType]) {
        person.opportunities = person.opportunities.filter(opp => opp.type === filter)
        delete person[otherFilterType]
      }
      return person[filterType]
    })
  }

  async function getUserOpportunitiesFromPOP() {
    Home.RecommendedTasks.callingPOP = true
    const segment = await FS.User.getSegmentData()
    let url = `/service/home/pop/users/${FS.User.getEffectiveId()}/opportunities/summaries?includeVisited=false`
    if (FS.showEx('beginnerClosenessParamRTEx') && (getIsNewUserOverride() || segment.newUser) ) { //override is for testing
      url += `&sortBy=CLOSENESS`
    }

    // used to prevent cached results when a person or activity is removed
    const headers = {
      'pragma': 'no-cache',
      'cache-control': 'no-cache'
    }
    let formattedOpportunities = []
    try {
      const { responseData } = await Home.xhr.get(url, headers)
      if (responseData && responseData.personSummaries) { // if false, most likely a 204 was returned.
        formattedOpportunities = responseData.personSummaries
          .map(formatDataMap, {filter:'all'})
          .filter(person => person.opportunities.length > 0 )
      }
    } catch (error) {
      //This error rarely happens and triggers an infinite spinner so we're ignoring it for now
      console.error(error)
    }
    return formattedOpportunities
  }

  /**
   * Fetches the users opportunities from POP, and then stores the formatted tasks by type
   * in the following global variables:
   * `Home.RecommendedTasks.unfilteredTaskData`
   * `Home.RecommendedTasks.recordTaskData`
   * `Home.RecommendedTasks.templeTaskData`
   *
   * @returns {Promise<void>} an empty promise
   */
  async function setUserTasks() {
    if (Home.RecommendedTasks.unfilteredTaskData) return

    const opportunities = await getUserOpportunitiesFromPOP()
    const tasks = await requestAndMergeTreeDataWithTasks(opportunities)
    Home.RecommendedTasks.unfilteredTaskData = tasks
    Home.RecommendedTasks.recordTaskData = getFilteredOpportunities(tasks, 'RECORD')
    Home.RecommendedTasks.templeTaskData = getFilteredOpportunities(tasks, 'TEMPLE')
  }

  /**
   * Filter for living people and people who don't have a name
   *
   * @param {object} treeData Response data from batch tf summary call
   * @param {object} personWithTasks Task data
   * @returns {boolean} `true` if the person is not living and has a name, else `false`
   */
  function filterPeople(treeData, personWithTasks) {
    if (treeData[personWithTasks.personId].status === 'RESTRICTED') return false
    if (!treeData[personWithTasks.personId].personCardSummary) return false
    const personTFData = treeData[personWithTasks.personId].personCardSummary.summary
    // Adding hasNoName variable to filter out persons who don't have a name listed
    const hasNoName = personTFData.name === '' || personTFData.name === '?'
    return (!personTFData.living && !hasNoName)
  }

  /**
   * Gets the tree data and combines it with the ancestors we have tasks for
   *
   * @param {array} newPersonsWithTasks all of the people we have tasks for
   * @returns {Promise} promise with the updated people with tree data
   */
  async function requestAndMergeTreeDataWithTasks(newPersonsWithTasks) {
    const result = []
    try {
      if (!newPersonsWithTasks || !newPersonsWithTasks.length) return []
      let newPersonsBatches = []
      if (newPersonsWithTasks.length > 500) {
        newPersonsBatches = chunkArray(newPersonsWithTasks, 500)
      } else {
        newPersonsBatches.push(newPersonsWithTasks)
      }

      await asyncForEach(newPersonsBatches, async (personTasks) => {
        // Assemble the query params in the form: ?id=PID1&id=PID2
        const url = '/service/tree/tf/person/summary?featureTag=GenericRelationshipTerms&id=' + personTasks.map(personWithTasks => {
          return personWithTasks.personId
        }).join('&id=')
        const { status, responseData } = await Home.xhr.get(url)
        if (status === 200) {
          const filteredData = personTasks
            .filter(filterPeople.bind(null, responseData))
            .map(personWithTasks => {
              const summaryData = responseData[personWithTasks.personId].personCardSummary.summary
              personWithTasks.name = summaryData.name
              personWithTasks.lifespan = summaryData.lifespan
              personWithTasks.sex = summaryData.gender
              return personWithTasks
            })
          result.push(...filteredData)
        }
      })
    } catch (error) {
      // This error rarely happens
      console.error(error)
    }
    return result
  }

  /**
   * Calculate whether the user has any tasks for the given type.
   * Used to decide whether to enable the filter buttons.
   *
   * @param {String} taskType Task type
   * @returns {Boolean} Whether the user has any tasks of the given type.
   */
  // eslint-disable-next-line complexity
  async function hasTasksOfType(taskType = '') {
    try {
      let hasTaskType = false
      await setUserTasks()

      const { unfilteredTaskData: allTasks } = Home.RecommendedTasks

      // Return false if we have no tasks
      if (!allTasks || !allTasks.length) {
        return hasTaskType
      }

      // Return true if there are tasks of the given type
      for (const task of allTasks) {
        if ( (taskType.toLowerCase() === "temple" && task.hasTemple)
            || (taskType.toLowerCase() === "record" && task.hasRecord) ) {
          hasTaskType = true
        }
      }
      return hasTaskType
    } catch (error) {
      console.error('Error getting tasks:', error);
      return false;
    }
  }

  /**
   * Get temple ordinance data from Temple Icon Cache to populate the green
   * temple dropdown ordinance squares and give the reason why an orange temple
   * (in its dropdown) is not ready.
   *
   * @param {string} pid personId of the
   * @returns {void}
   */
  async function getTempleOrdinances(pid) {
    try {
      // Note: eventually this header will not be needed and will become the default behavior, but it's needed for now
      let headers = {};
      if (FS.showEx('templeSharedGreenEx')) {
        headers = {'X-FS-Feature-Tag': 'reservationsSharedReadyEx'}
      }
      const { status, responseData } = await Home.xhr.get('/service/tree/tree-data/reservations/person/' + pid + '/card', headers)
      if (status === 200) {
        return responseData;
      }
      throw new Error("Get Temple Ordinances Network Call Error:", status)
    } catch (error) {
      throw new Error("Get Temple Ordinances Network Call Error: " + error.status)
    }
  }

  Home.RecommendedTasks = RecommendedTasks

  // Setup static methods
  RecommendedTasks.dismissTask = dismissTask
  RecommendedTasks.undoDismissTask = undoDismissTask
  RecommendedTasks.getTempleOrdinances = getTempleOrdinances
  RecommendedTasks.hasTasksOfType = hasTasksOfType
  RecommendedTasks.refreshOpportunities = refreshOpportunities
  RecommendedTasks.setUserTasks = setUserTasks
}())
